import { forwardRef, useRef, useState, useEffect } from "react";
import { DeleteIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Input,
  InputGroup,
  Popover,
  PopoverBody,
  PopoverContent,
  Stack,
  Text,
  PopoverAnchor,
  SystemStyleObject,
  InputLeftElement,
} from "@chakra-ui/react";
import { isEmpty } from "ramda";

import useApiEndpoint from "hooks/useApiEndpoint";
import useClickOutside from "hooks/useClickOutside";
import useDebouncedCallback from "hooks/useDebouncedCallback";
import useSearchLocationHistory from "hooks/useSearchLocationHistory";
import { DEFAULT_LOCATION } from "scenes/Search/consts";
import AutocompleteApiEndpoint, {
  TOutput as TSuggestionApiOutput,
} from "utils/geo/api/Autocomplete";
import { useTracking } from "contexts/Tracking";
import IconPin2 from "./Icons/Pin2";

const normalizeLocation = (location: string) =>
  location
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll(", ", "-")
    .replaceAll(",", "-")
    .replaceAll(" ", "-")
    .trim();

const SearchInput = forwardRef<
  HTMLInputElement,
  {
    initialValue?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    onFocus: (isFocused: boolean, value: string) => void;
    placeholder: string;
    onEnter?: (value: string) => void;
    value?: string;
    isInFilter?: boolean;
    sx?: SystemStyleObject;
    variant?: "DEFAULT" | "DARK";
  }
>(
  (
    {
      onChange,
      onFocus,
      placeholder,
      onEnter,
      value,
      isInFilter,
      onBlur,
      sx,
      variant,
    },
    ref,
  ) => (
    <Input
      sx={{
        ...sx,
        "::placeholder": {
          color: "ud-grey-300",
        },
      }}
      id="location-search-input"
      ref={ref}
      value={value}
      placeholder={placeholder}
      _placeholder={{ fontWeight: "normal" }}
      color={variant === "DEFAULT" ? "ud-grey-600" : "ud-white"}
      bg={variant === "DEFAULT" ? "white" : "ud-grey-dark"}
      border="0"
      pl="32px"
      _focus={{ boxShadow: "0" }}
      autoCorrect="off"
      autoComplete="off"
      fontWeight={isInFilter ? "normal" : "bold"}
      borderRadius="inherit"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          // @ts-expect-error Ignore type
          onEnter && onEnter(e.target?.value || "");
        }
      }}
      onFocus={(e) => {
        onFocus(true, e.target.value);
      }}
      onChange={({ currentTarget: { value: val } }) => {
        // handleDebouncedValue(value);
        onChange && onChange(val);
      }}
      onBlur={onBlur}
    />
  ),
);

const LocationSuggest: React.FC<{
  onLocationSelect?: (location: string) => void;
  onValueChange?: (location: string, isDisabledSubmit: boolean) => void;
  defaultSearchLocation?: string;
  buttonText?: string;
  width?: string;
  onClear?: () => void;
  changeInputOnPick?: boolean;
  placeholder?: string;
  isInFilter?: boolean;
  locationValue?: string;
  error?: boolean;
  sx?: SystemStyleObject;
  isLastSearchLocation?: boolean;
  variant?: "DEFAULT" | "DARK";
}> = ({
  onLocationSelect,
  onValueChange,
  defaultSearchLocation = DEFAULT_LOCATION,
  isInFilter,
  onClear,
  changeInputOnPick,
  placeholder = "Město, ulice...",
  width,
  locationValue,
  error = false,
  isLastSearchLocation = false,
  variant = "DEFAULT",
  sx,
}) => {
  const [value, setValue] = useState(locationValue || "");
  const [items, setItems] = useState<TSuggestionApiOutput>([]);
  const [hasError, setError] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(false);

  const popperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { trackEvent } = useTracking();
  const { loadedValues, saveNewLocation, removeFromHistory } =
    useSearchLocationHistory();

  const getColor = () => {
    if (isDisabledSubmit) {
      return "ud-error";
    }
    if (error) {
      return "ud-error";
    }

    if (error) {
      return "ud-error";
    }

    return "ud-grey-300";
  };

  const { callEndpoint: autocompleteApi, cancelRequest } = useApiEndpoint(
    AutocompleteApiEndpoint,
  );

  const newLoadedValues = loadedValues.slice(0, 4);

  const closeModal = (): void => {
    setOpen(false);
  };

  const handleValueCheck = (
    enteredValue: string,
    suggestionItems?: TSuggestionApiOutput | null,
  ) => {
    if (!suggestionItems?.length) {
      setIsDisabledSubmit(true);
      onValueChange && onValueChange(enteredValue, true);

      return;
    }

    const itemMatchingValue = suggestionItems.find(
      ({ label }) =>
        normalizeLocation(label) === normalizeLocation(enteredValue),
    );
    setIsDisabledSubmit(!itemMatchingValue);
    onValueChange?.(
      itemMatchingValue?.label || enteredValue,
      !itemMatchingValue,
    );
  };

  useClickOutside(
    popperRef,
    () => {
      closeModal();
    },
    [inputRef],
  );

  const findProducts = useDebouncedCallback(async (enteredValue: string) => {
    cancelRequest();
    setError(false);

    autocompleteApi({ term: enteredValue })
      .then((data) => {
        setError(Boolean(isEmpty(data.data || [])));
        setItems(data?.data || []);
        handleValueCheck(enteredValue, data?.data);
      })
      .catch(() => {
        setError(true);
      });
  }, 70);

  const handleChange = (inputValue: string) => {
    if (inputValue === "" && value.length !== 0) {
      setTimeout(() => {
        onClear && onClear();
        setIsDisabledSubmit(false);
      }, 500);
    }
    setValue(inputValue);
    setError(false);
    if (!inputValue) {
      setItems([]);

      return;
    }
    setOpen(true);
    findProducts(inputValue);
  };

  const onSelect = (location: string): void => {
    const isFind = items.find(
      (item) => item.label.toLocaleLowerCase() === location.toLocaleLowerCase(),
    );
    if (!isFind) {
      if (items.length) {
        location = items[0].label;
      } else if (location.length) {
        location = location;
      } else {
        location = newLoadedValues[0];
      }
    }

    const newLocation =
      location ||
      (Boolean(newLoadedValues.length)
        ? newLoadedValues[0]
        : defaultSearchLocation);
    defaultSearchLocation !== newLocation && saveNewLocation(newLocation);
    if (onLocationSelect) {
      onLocationSelect(newLocation);
      setValue(newLocation);
    }
    setIsDisabledSubmit(false);

    if (changeInputOnPick) {
      setValue(newLocation);
    }
    closeModal();
  };

  useEffect(() => {
    if (loadedValues.length && isLastSearchLocation) {
      setValue(loadedValues[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedValues]);

  return (
    <Popover
      isOpen={isOpen && !hasError}
      autoFocus={false}
      closeOnBlur={false}
      placement="bottom-start"
      isLazy
      matchWidth
    >
      <PopoverAnchor>
        <Box
          maxW={width}
          p={0}
          w="100%"
          bg="white"
          borderRadius="7px"
          position="relative"
        >
          <InputGroup
            border={isInFilter ? "1px" : undefined}
            borderColor={getColor()}
            borderRadius="7px"
            data-test="searchAdressInput"
          >
            <InputLeftElement pointerEvents="none">
              <IconPin2 fill="ud-grey-500" boxSize="16px" />
            </InputLeftElement>
            <SearchInput
              sx={sx}
              ref={inputRef}
              value={value}
              variant={variant}
              placeholder={placeholder}
              onEnter={(location) => !isDisabledSubmit && onSelect(location)}
              onChange={handleChange}
              onBlur={() => setOpen(false)}
              onFocus={(isFocused, val) => {
                setOpen(isFocused);
                if (!val) {
                  setItems([]);
                }
              }}
            />
          </InputGroup>
          {isDisabledSubmit && isInFilter && (
            <Text variant="input-error" pt="4px" position="absolute">
              Tuto lokalitu neznáme
            </Text>
          )}
        </Box>
      </PopoverAnchor>
      <PopoverContent
        w="100%"
        my="-5px"
        backgroundColor={variant === "DEFAULT" ? "ud-white" : "ud-grey-dark"}
        borderColor={variant === "DEFAULT" ? "inherit" : "ud-grey-600"}
      >
        <PopoverBody ref={popperRef} _focus={{ boxShadow: "0" }}>
          {((!hasError && isEmpty(items)) || value.length <= 2) && (
            <Box p="10px">
              <Text
                variant="small_1"
                color={variant === "DEFAULT" ? "ud-grey-600" : "ud-grey-300"}
              >
                Začněte psát a my vám zkusíme poradit.
              </Text>
            </Box>
          )}

          {isEmpty(items) && !isEmpty(newLoadedValues) && (
            <>
              <Divider />
              <Text
                px="10px"
                mt="10px"
                mb="5px"
                fontWeight="semibold"
                color={variant === "DEFAULT" ? "ud-grey-600" : "ud-grey-300"}
              >
                Z Vašeho posledního vyhledávání
              </Text>
            </>
          )}

          <Box _last={{ div: { borderBottomRadius: "7px" } }}>
            {(isEmpty(items)
              ? newLoadedValues
              : (items as Array<string | TSuggestionApiOutput[0]>)
            ).map((item, i) => {
              const isFromHistory = typeof item === "string";
              const name = typeof item === "string" ? item : item.label;

              return (
                <Stack
                  key={i}
                  px="15px"
                  py="10px"
                  direction="row"
                  align="center"
                  justify="space-between"
                  transition="all 333ms ease 0s"
                  _hover={{
                    background:
                      variant === "DEFAULT" ? "#F1F1F1" : "ud-grey-500",
                    cursor: "pointer",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    trackEvent({
                      "dl.eCat": "homepage",
                      "dl.eAct": "link.clicked",
                      "dl.eLab": `Hledat bydlení ${value}`,
                    });
                    onSelect(name);
                  }}
                >
                  <Stack direction="row" align="center">
                    {isFromHistory && (
                      <SearchIcon
                        boxSize="20px"
                        mt="-3px"
                        pr="8px"
                        _hover={{
                          color:
                            variant === "DEFAULT" ? "ud-black" : "ud-grey-300",
                        }}
                        color={
                          variant === "DEFAULT" ? "ud-black" : "ud-grey-300"
                        }
                      />
                    )}
                    <Text
                      color={
                        variant === "DEFAULT" ? "ud-grey-600" : "ud-grey-300"
                      }
                    >
                      {name}
                    </Text>
                  </Stack>

                  {isFromHistory && (
                    <DeleteIcon
                      p="6px"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromHistory(name);
                        inputRef.current?.focus();
                      }}
                      _hover={{
                        color:
                          variant === "DEFAULT" ? "#BC2010" : "ud-grey-300",
                      }}
                      color={variant === "DEFAULT" ? "#BC2010" : "ud-grey-300"}
                      boxSize="26px"
                    />
                  )}
                </Stack>
              );
            })}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default LocationSuggest;
